import { useEffect, useRef } from 'react';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMagnifyingGlass, faXmark } from '@fortawesome/free-solid-svg-icons';
import { P, fs, radius } from '../theme/tokens';

const Wrap = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: ${(p) => p.$minWidth || 260}px;
  padding: 8px 16px;
  background: ${P.white};
  border: 1px solid ${P.border};
  border-radius: ${(p) => (p.$square ? radius.md : radius.pill)}px;
  transition: border-color 120ms, box-shadow 120ms;

  &:focus-within {
    border-color: ${P.navy};
    box-shadow: 0 0 0 3px rgba(30, 58, 95, 0.12);
  }
  svg {
    font-size: 12px;
    color: ${P.subtle};
  }
`;

const Input = styled.input`
  flex: 1;
  min-width: 0;
  border: none;
  outline: none;
  background: transparent;
  font-family: inherit;
  font-size: ${fs.sm}px;
  color: ${P.ink};

  &::placeholder {
    color: ${P.subtle};
  }
  &::-webkit-search-cancel-button {
    display: none;
  }
`;

const Clear = styled.button`
  display: grid;
  padding: 2px;
  border: none;
  background: none;
  cursor: pointer;
`;

const Hint = styled.kbd`
  display: grid;
  place-items: center;
  min-width: 20px;
  height: 20px;
  padding: 0 5px;
  border: 1px solid ${P.border};
  border-radius: 5px;
  font-family: inherit;
  font-size: 11px;
  color: ${P.subtle};
  background: ${P.gray100};
`;

export default function SearchInput({ value, onChange, onClear, placeholder = 'Search…', hint, minWidth, square, style }) {
  const ref = useRef(null);

  useEffect(() => {
    if (!hint) return undefined;
    const onKey = (e) => {
      const tag = document.activeElement?.tagName;
      if (e.key !== hint || tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
      e.preventDefault();
      ref.current?.focus();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [hint]);

  return (
    <Wrap $minWidth={minWidth} $square={square} style={style}>
      <FontAwesomeIcon icon={faMagnifyingGlass} />
      <Input
        ref={ref}
        type="search"
        value={value}
        aria-label={placeholder}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
      />
      {onClear && value && (
        <Clear type="button" aria-label="Clear search" onClick={onClear}>
          <FontAwesomeIcon icon={faXmark} />
        </Clear>
      )}
      {hint && <Hint aria-hidden="true">{hint}</Hint>}
    </Wrap>
  );
}
